import { find } from 'lodash';

/**
 * CVX-6218: resolving a served translation against the viewer's language.
 *
 * Deliberately a plain module rather than a service: it imports nothing but
 * lodash, so it is unit-testable without pulling the `@services` barrel (which
 * cannot be loaded from a spec — see check-detail.service.spec.ts). Callers
 * pass the viewer language in; resolving it is UserdataService's job.
 *
 * The two shapes come from two different backend contracts:
 *
 *   observation_notes — `translations` sits on each note and its `value` is
 *     the translated string.
 *   response_items    — `translations` sits on the response item and its
 *     `value` is the WHOLE `notes` array, because the stored form mirrors the
 *     original column so a client parses both the same way.
 *
 * In both cases the backend sends at most one entry and omits the field when
 * there is nothing to serve, so the fallback is always the untouched original.
 */

/** True when the row carries a source language that differs from the viewer's. */
function substitutable(row, viewerLanguage: string): boolean {
  return !!row?.language && row.language !== viewerLanguage;
}

/**
 * An observation note's text in the viewer's language, or the original.
 *
 * An EMPTY translated string falls back to the original rather than blanking
 * the note. `??` alone would not: it is nullish-only, and the backend can store
 * `''` — `_forDb` passes any string through untouched and `_units` falls back
 * with `//`, which accepts `''` as a real value. A provider returning an empty
 * string for one unit would otherwise erase that note everywhere it is shown
 * read-only, with no control to get it back.
 */
export function translatedNoteValue(note, viewerLanguage: string): string {
  if (!substitutable(note, viewerLanguage)) {
    return note?.value;
  }
  return find(note.translations, { language: viewerLanguage })?.value || note.value;
}

/**
 * A response item's `notes` array in the viewer's language, or the original.
 *
 * The backend rebuilds the array index-by-index, so order and length are
 * preserved — but it hands EVERY unit to the translator, audio units included,
 * and an audio unit's `value` is an objectID that must survive byte-identical
 * or the URI built from it breaks. So the translated text is taken only where
 * the unit is actually text, and a length mismatch discards the translation
 * rather than risking a misaligned merge.
 */
export function translatedNoteUnits(item, viewerLanguage: string): any[] {
  const original = item?.notes || [];
  if (!substitutable(item, viewerLanguage)) {
    return original;
  }

  const translated = find(item.translations, { language: viewerLanguage })?.value;
  if (!translated || translated.length !== original.length) {
    return original;
  }

  // `||`, not `??`, for the same reason as translatedNoteValue: an empty
  // translated string must fall back to the original rather than blank the unit.
  return original.map((unit, i) =>
    unit?.type === 'text' ? { ...unit, value: translated[i]?.value || unit.value } : unit);
}
